import { useMutation, useQueryClient } from '@tanstack/react-query';
import { rolloverService, RolloverResult } from '@/services/leave/rolloverService';
import { rolloverAuditService, RolloverPreview } from '@/services/leave/rolloverAuditService';
import { leaveQueryKeys } from './queryKeys';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';

export const useRolloverMutations = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [previewData, setPreviewData] = useState<RolloverPreview | null>(null);

  const processAnnualRollover = useMutation({
    mutationFn: async (rolloverYear?: number) => {
      // Execute rollover with full audit trail
      const result = await rolloverAuditService.executeRolloverWithAudit(rolloverYear);
      return result;
    },
    onMutate: () => {
      console.log('[useRolloverMutations] Starting annual rollover process with audit...');
    },
    onSuccess: (result: { auditLogId: string; result: RolloverResult }) => {
      console.log('[useRolloverMutations] Rollover completed successfully:', result);
      
      // Invalidate all balance-related queries to refresh data
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.balances() });
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.policies() });
      queryClient.invalidateQueries({ queryKey: ['rollover-audit-logs'] });
      
      toast({
        title: "Annual Rollover Completed",
        description: `Successfully processed rollover for ${result.result.processed_count} employees from ${result.result.rollover_year} to ${result.result.target_year}`,
      });
      
      // Clear preview data after successful rollover
      setPreviewData(null);
    },
    onError: (error: Error) => {
      console.error('[useRolloverMutations] Rollover failed:', error);
      
      let errorMessage = "Failed to process annual rollover";
      if (error.message.includes('Unauthorized')) {
        errorMessage = "You don't have permission to process company rollovers";
      } else if (error.message.includes('Company ID required')) {
        errorMessage = "Company information not found. Please refresh and try again.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Rollover Failed", 
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const generatePreview = useMutation({
    mutationFn: (rolloverYear?: number) => rolloverAuditService.generateRolloverPreview(rolloverYear),
    onSuccess: (preview: RolloverPreview) => {
      console.log('[useRolloverMutations] Preview generated successfully:', preview);
      setPreviewData(preview);
      
      toast({
        title: "Preview Generated",
        description: `Preview shows ${preview.employee_count} employees will be affected`,
      });
    },
    onError: (error: Error) => {
      console.error('[useRolloverMutations] Preview generation failed:', error);
      
      toast({
        title: "Preview Failed", 
        description: error.message || "Failed to generate rollover preview",
        variant: "destructive",
      });
    },
  });

  return {
    processAnnualRollover,
    generatePreview,
    previewData,
    isProcessing: processAnnualRollover.isPending,
    isGeneratingPreview: generatePreview.isPending,
  };
};